/* General Request and Error Types */

export interface APIStandardError {
  code: string;
  detail: string;
  http_status_code: number;
  non_field_errors?: Array<string>;
  [k: string]: Array<string> | any;
}
interface PaginationResponseDefaults {
  count: number;
  next: string | null;
  previous: string | null;
}

/* Core Data Types */

interface User {
  id: number;
  last_login: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  date_joined: string;
}

interface Account {
  id: number;
  tp_account_number: string;
  tp_routing_number: string;
}
interface Enrollment {
  id: number;
  timestamp_created: string;
  user: User;
  account: Account;
  is_archived: boolean;
  sponsored_savings_program: number;
  ssp_enrolled_at: string;
  ssp_ygbm_monthly_contribution: string;
  ssp_ygbm_monthly_contribution_history: [
    {
      timestamp: string;
      ssp_ygbm_monthly_contribution: string;
      sponsored_savings_autoswitch_directdeposit_user_agreed: boolean;
    }
  ];
}

interface Program {
  id: number;
  name: string | null;
  budget: string | null;
  ygbm_goal: string | null;
  ygbm_reward: string | null;
  ygbm_monthly_max_contrib: string | null;
  no_spend_monthly_match_bonus: string | null;
  default_contrib_amount: string | null;
  sign_up_bonus_amount: string | null;
  referral_bonus_amount: string | null;
  offer_debit_card: boolean;
  send_debit_card_by_default: boolean;
  is_active: null;
}

export interface Employer {
  id: number;
  timestamp_created: string;
  name: string | null;
  logo: string | null;
  phone_number: string | null;
  slug: string | null;
  total_number_of_employees: number | null;
  sponsored_savings_autoswitch_directdeposit: boolean;
  employee_signup_bonus_amount: number | null;
  coworker_referral_program_referral_amount: number | null;
  payroll_system: string | null;
  integration_method: "file_upload" | "manual" | null;
  employee_list_file: string | null;
  created_by_user: number | null;
  sponsored_savings_program: number | null;
}

export type ProgramTypeT = "ygbm";
export type ProgramStatusTypeT = "ontrack" | "offtrack" | "complete" | "missed";

export interface SponsoredSavings {
  enabled: boolean;
  program: {
    id: number;
    name: string;
    program_type: ProgramTypeT;
    ygbm_goal: string;
    ygbm_reward: string;
    ygbm_monthly_max_contrib: string;
    no_spend_monthly_match_bonus: string | null;
    employer_program_sign_up_bonus_amount: string | null;
    employer_program_referral_bonus_amount: string | null;
  } | null;
  is_enrolled: boolean;
  ssp_enrollment: {
    id: number;
    timestamp_created: string;
    user: number;
    account: number;
    is_archived: boolean;
    sponsored_savings_program: number;
    ssp_enrolled_at: string;
    ssp_ygbm_monthly_contribution: string;
    ssp_ygbm_monthly_contribution_history: Array<{
      timestamp: string;
      ssp_ygbm_monthly_contribution: string;
      sponsored_savings_autoswitch_directdeposit_user_agreed: boolean;
    }>;
    sponsored_savings_status: {
      next_program_start_timestamp: string;
      current_program_start_timestamp: string;
      current_program_balance_at_start_timestamp: string;
      current_program_months_till_end: number;
      current_program_net_contributions: string;
      current_program_min_contribution_to_hit_goal: string;
      current_program_months_to_hit_goal: number;
      current_program_estimated_savings: string;
      current_program_status: ProgramStatusTypeT;
    };
    sponsored_savings_matches: {
      earned_sponsored_savings_bonus: boolean;
      earned_sign_up_bonus: boolean;
      no_spend_monthly_match_history: Array<{
        month: string;
        month_name: string;
        has_payout: boolean;
      }>;
    };
  } | null;
}

export interface Transaction {
  id: number;
  user: number;
  tp_trace_id: string | null;
  timestamp: string;
  timestamp_readable: string;
  amount: string;
  description: string;
  description_readable: string;
  tp_type: string | null;
  annotation_ach_type: string | null;
  type: string;
  tp_book_id: string | null;
  is_payout: boolean;
}

export enum DebitCardStatusEnum {
  active = "active",
  unactivated = "unactivated",
  suspended = "suspended",
}

export interface DebitCardDetails {
  id: number;
  tp_id: string;
  tp_status: DebitCardStatusEnum;
  tp_account_id: string;
  tp_card_product_id: string;
  tp_person_id: string;
  tp_expiration: string;
  tp_pin_is_set: boolean;
  estimated_arrival_date: string | null;
  bank: "lcb" | "piermont";
}

export interface ExternalAccount {
  id: number;
  timestamp_created: string;
  user: number;
  nickname: string;
  plaid_name?: string;
  plaid_official_name?: string;
  plaid_institution_id?: string;
  plaid_institution_name?: string;
  plaid_institution_logo?: string;
}

export interface AchTransferItem {
  id: number;
  timestamp: string;
  amount: string;
  simple_status: "PENDING" | "ERROR" | "COMPLETE";
  simple_status_error_type_msg: string;
  account: {
    id: string;
    name: string;
  };
  direction: TransferDirection;
}

export interface AccountStatementListItem {
  url_slug: string;
  month_name_readable: string;
}

export interface UserReferral {
  id: number;
  referred_user: number;
  referred_name: string;
  status: "onboarding" | "completed";
  payout_amount: string | null;
}

export interface UserMailingAddress {
  shipping_address: {
    street: string;
    unit: string | null;
    city: string;
    state: string;
    zip_code: string;
  };
}

/* Query / Mutation Types */
export interface QueryStandardSuccessResponse {
  success: boolean;
}
export interface QueryAdminDashboardResponse {
  is_onboarding_complete: boolean;
  is_program_live: boolean;
  stats?: {
    annualized_savings: number | null;
    average_monthly_contribution: number | null;
    total_savings: number | null;
    average_savings_balance: number | null;
    sign_ups_last_30_days: number | null;
    percent_receiving_bonus_in_last_30_days: number | null;
    total_participants: number | null;
    participants_count_over_time: Array<{
      month: string;
      participants: number;
    }> | null;
  };
}

export interface QueryAdminDashboardEmployeesResponse
  extends PaginationResponseDefaults {
  results: Array<Enrollment> | [];
}

export interface QueryAdminDashboardProgramsResponse extends Program {}

export interface MutationAdminCompanySettingsRequestBody {
  name: Employer["name"];
  phone_number: Employer["phone_number"];
}
export interface QueryAdminCompanySettingsResponse extends Employer {}

export interface TeamMemberSetting {
  id: number;
  timestamp_created: string;
  user: User;
  access: "full_access";
  invite_status: "invited" | "accepted";
  employer: number;
}

export interface QueryAdminTeamSettingsResponse
  extends PaginationResponseDefaults {
  results: Array<TeamMemberSetting> | [];
}

export interface MutationAdminInviteNewTeamMemberRequestBody {
  email: string;
  first_name: string;
  last_name: string;
}

export interface MutationAdminInactivateTeamMemberRequestBody {
  user_to_inactivate: number;
}

export interface MutationAdminChangePasswordRequestBody {
  old_password: string;
  new_password: string;
}

export interface QueryMeResponse {
  user: User;
  role: "admin" | "employee";
  employer: {
    id: Employer["id"];
    name: Employer["name"];
  };
}

export interface QueryAccountStatusResponse {
  status: "account_active" | string;
}

export interface QueryAccountResponse {
  user: {
    first_name: string;
    last_name: string;
    email: string;
    uuid: string;
  };
  employer: {
    id: number;
    name: string;
    employee_signup_bonus_amount: string | null;
    sponsored_savings_autoswitch_directdeposit: boolean;
    coworker_referral_program: {
      feature_flag: boolean;
      referral_amount: string | null;
      referral_url: string;
    };
  };
  challenger_saving: {
    account_number: string;
    routing_number: string;
    account_available_balance: string; // funds in account minus holds (always show this)
    account_current_balance: string; // funds in the account
    recent_transactions: Array<Transaction>;
    sponsored_savings: SponsoredSavings;
    debit_card: {
      program_offer_debit_cards_enabled: boolean;
      latest_card_unactivated: boolean;
      latest_card_id: number;
      latest_card: DebitCardDetails;
    };
  };
}

export interface AccountBalanceItem {
  period: string;
  period_balance: number;
}

export interface AccountContributionsItem {
  total_contributions: number;
  total_matches: number;
}

export interface AccountWithdrawalsItem {
  total_withdrawals: number;
  total_deposits: number;
}

export type ChartTypePeriod = "30d" | "1y" | "all";
export type ChartTypeQuery = "balances" | "contributions" | "withdrawals";
export interface QueryAccountBalanceResponse
  extends Array<
    AccountBalanceItem & AccountContributionsItem & AccountWithdrawalsItem
  > {}

export interface QueryExternalAccountResponse
  extends PaginationResponseDefaults {
  results: Array<ExternalAccount> | [];
}
export interface QueryAchTransferHistoryResponse
  extends Array<AchTransferItem> {}

export type TransferDirection = "add_money" | "send_money";

export interface QueryAccountStatementsResponse
  extends Array<AccountStatementListItem> {}

export interface QueryUserReferralsResponse extends Array<UserReferral> {}

export interface QueryUserMailingAddressResponse extends UserMailingAddress {}

export interface MutationOrderNewCardResponse {
  status: "success" | "error";
  card: DebitCardDetails;
}

export interface QueryTransactionsResponse extends PaginationResponseDefaults {
  results: Array<Transaction> | [];
}
